import { createStart, createMiddleware } from "@tanstack/react-start";
import { setResponseHeader } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

function recordGeoblockEvent(row: {
  country: string;
  path: string;
  method: string;
  ua: string;
  referer: string | null;
  ip: string | null;
  action: "block" | "allow_bot";
}) {
  // Fire-and-forget; never block the request on DB latency or failure.
  try {
    void supabaseAdmin
      .from("geoblock_events")
      .insert(row)
      .then(({ error }) => {
        if (error) console.log(JSON.stringify({ tag: "GEOBLOCK", action: "db_error", message: error.message }));
      });
  } catch (e) {
    // supabaseAdmin proxy may throw if env missing (local dev). Swallow.
  }
}

/**
 * Country-level allowlist.
 *
 * Why: Analytics showed ~75% of traffic was scraper bots from CN/HK/BR/IN/PK
 * with near-zero conversion. The business serves Colorado / North America,
 * so we flip from a small blocklist to an allowlist: only countries that
 * could plausibly produce a customer, partner, or referral get through.
 *
 * How: Cloudflare adds a `cf-ipcountry` header (ISO 3166-1 alpha-2) to every
 * request. Known search-engine + AI crawlers bypass via user-agent regardless
 * of country, so SEO is never affected even though we block CN/RU/etc.
 *
 * To adjust: edit ALLOWED_COUNTRIES below. To disable entirely, return early.
 */
const ALLOWED_COUNTRIES = new Set([
  // Primary market
  "US", "CA", "MX",
  // Western Europe (legitimate referral / press / agency traffic)
  "GB", "IE", "FR", "DE", "ES", "IT", "NL", "BE", "LU",
  "CH", "AT", "PT", "SE", "NO", "DK", "FI", "IS",
  // Oceania
  "AU", "NZ",
  // US territories
  "PR", "VI", "GU", "AS", "MP",
]);

// Search-engine bots we always let through, regardless of geo. Googlebot
// crawls primarily from US IPs but we keep this as a safety net.
// Known good crawlers — always allowed through, even from blocked countries.
// Includes traditional search engines, social preview bots, and AI/LLM
// crawlers we want indexing the site (ChatGPT, Claude, Perplexity, Gemini).
const ALLOWED_BOT_UA = /googlebot|google-inspectiontool|bingbot|adsbot-google|mediapartners-google|slurp|duckduckbot|baiduspider|yandex|applebot|facebookexternalhit|twitterbot|linkedinbot|petalbot|sogou|ahrefsbot|semrushbot|mj12bot|dotbot|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-web|anthropic-ai|perplexitybot|perplexity-user|google-extended|bytespider|amazonbot/i;

const geoBlockMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/") || url.pathname === "/email/unsubscribe") return next();
  const country = request.headers.get("cf-ipcountry")?.toUpperCase();
  // No header = not behind Cloudflare (preview, local dev). Let it through.
  if (country && !ALLOWED_COUNTRIES.has(country)) {
    const ua = request.headers.get("user-agent") ?? "";
    if (!ALLOWED_BOT_UA.test(ua)) {
      // Structured log — searchable in worker logs via "GEOBLOCK".
      // Captures country, path, UA, referer, and IP for auditing bot
      // patterns and spotting false positives (real users misclassified).
      console.log(
        JSON.stringify({
          tag: "GEOBLOCK",
          action: "block",
          country,
          path: url.pathname + url.search,
          method: request.method,
          ua: ua.slice(0, 200),
          referer: request.headers.get("referer") ?? null,
          ip: request.headers.get("cf-connecting-ip") ?? null,
          ts: new Date().toISOString(),
        }),
      );
      recordGeoblockEvent({
        country,
        path: url.pathname + url.search,
        method: request.method,
        ua: ua.slice(0, 500),
        referer: request.headers.get("referer"),
        ip: request.headers.get("cf-connecting-ip"),
        action: "block",
      });
      return new Response("Forbidden", {
        status: 403,
        headers: { "Cache-Control": "public, max-age=86400" },
      }) as never;
    }
    // Crawler bypass — log so we can confirm SEO bots aren't blocked.
    console.log(
      JSON.stringify({
        tag: "GEOBLOCK",
        action: "allow_bot",
        country,
        path: url.pathname,
        ua: ua.slice(0, 200),
        ts: new Date().toISOString(),
      }),
    );
    recordGeoblockEvent({
      country,
      path: url.pathname,
      method: request.method,
      ua: ua.slice(0, 500),
      referer: request.headers.get("referer"),
      ip: request.headers.get("cf-connecting-ip"),
      action: "allow_bot",
    });
  }
  return next();
});

/**
 * Force the correct Content-Type for data-format routes.
 *
 * Why: handlers under `src/routes/*[.]xml.ts`, `*[.]csv.ts`, and `robots[.]txt.ts`
 * each set their own `Content-Type`, but something downstream in the response
 * pipeline rewrites every one of them to `text/html; charset=utf-8`. That breaks
 * external parsers (Claude web-fetch, Google sitemap pingers, RSS readers,
 * validators) which key off the header, not the body sniff.
 *
 * Fix: after the handler runs, look at the URL extension and overwrite the
 * header. Only applies to 2xx responses so 404 HTML pages keep `text/html`.
 */
const CONTENT_TYPE_BY_EXT: Record<string, string> = {
  ".xml": "application/xml; charset=utf-8",
  ".csv": "text/csv; charset=utf-8",
  ".txt": "text/plain; charset=utf-8",
  ".json": "application/json; charset=utf-8",
};

const dataContentTypeMiddleware = createMiddleware().server(async ({ next, request }) => {
  const pathname = new URL(request.url).pathname;
  if (pathname.startsWith("/lovable/") || pathname === "/email/unsubscribe") return next();
  const result = await next();
  const dot = pathname.lastIndexOf(".");
  if (dot === -1) return result;
  const target = CONTENT_TYPE_BY_EXT[pathname.slice(dot).toLowerCase()];
  if (!target) return result;
  setResponseHeader("content-type", target);
  return result;
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [geoBlockMiddleware, dataContentTypeMiddleware],
}));
